import { useEffect, useState } from "react";

import { Box, Button, Chip, CircularProgress } from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from "react-router-dom";
import { PRIVATE_PAGES } from "@app/router/pages";
import { ApiEndpoint } from "@constants/index";
import DeleteButton from "common/DeleteButton";
import { ICategory } from "@modules/category/types";
import {
  useLazyGetCategoriesQuery,
  useDeleteCategoryMutation,
  useSortCategoriesMutation,
} from "@modules/category";
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";

import { StyledImg } from "./styled";

const Categories = () => {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [getCategories, { isLoading }] = useLazyGetCategoriesQuery();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [sortCategories] = useSortCategoriesMutation();

  const handleGetRequest = async () => {
    try {
      const response = await getCategories({ offset: 0, limit: 100 } as any).unwrap();

      setCategories(response?.categories || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      await deleteCategory({ id }).unwrap();
      handleGetRequest();
    } catch (error) {
      console.log(error);
    }
  };

  const sendSortToBackend = async (sortedList: ICategory[]) => {
    const payload = sortedList.map(({ id }, index) => ({ id, index }));

    await sortCategories({ list: payload });
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const clone = JSON.parse(JSON.stringify(categories)) as ICategory[];
    const sortedList = arrayMoveImmutable(clone, oldIndex, newIndex);

    setCategories(sortedList);
    sendSortToBackend(sortedList);
  };

  useEffect(() => {
    handleGetRequest();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <Link to={PRIVATE_PAGES.CREATE_CATEGORIES}>
        <Button variant="contained" color="primary">
          Create Category
        </Button>
      </Link>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="categories table">
          <TableHead>
            <TableRow
              sx={{
                display: 'grid',
                gridTemplateColumns: '100px 2fr 2fr 180px 160px',
              }}
            >
              <TableCell align="center">Image</TableCell>
              <TableCell align="left">Title</TableCell>
              <TableCell align="left">Description</TableCell>
              <TableCell align="center">Visibility</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              <SortableList
                onSortEnd={onSortEnd}
                lockAxis="y"
                draggedItemClassName="dragged"
              >
                {categories.map(({
                  id,
                  title,
                  titleAm,
                  titleEn,
                  titleRu,
                  description,
                  descriptionAm,
                  descriptionEn,
                  descriptionRu,
                  images,
                  visible,
                  visibleOnMap,
                }) => (
                  <SortableItem key={id}>
                    <TableRow
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '100px 2fr 2fr 180px 160px',
                        alignItems: 'center',
                        cursor: 'grab',
                        userSelect: 'none',
                        '&.dragged': {
                          background: '#f0f7ff',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          zIndex: 10,
                        },
                      }}
                    >
                      <TableCell align="center">
                        {images?.[0]?.url ? (
                          <StyledImg src={`${ApiEndpoint}/${images[0].url}`} alt={titleEn || titleAm || title || ''} />
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ fontWeight: 600 }}>{titleEn || titleAm || title || '-'}</Box>
                        <Box sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                          {titleAm && (
                            <span>
                              AM:
                              {titleAm}
                              {' '}
                            </span>
                          )}
                          {titleRu && (
                            <span>
                              | RU:
                              {titleRu}
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                        {descriptionEn || descriptionAm || descriptionRu || description || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={`Website: ${visible !== false ? 'Yes' : 'No'}`}
                            color={visible !== false ? 'success' : 'default'}
                            size="small"
                            variant={visible !== false ? 'filled' : 'outlined'}
                          />
                          <Chip
                            label={`Map: ${visibleOnMap !== false ? 'Yes' : 'No'}`}
                            color={visibleOnMap !== false ? 'info' : 'default'}
                            size="small"
                            variant={visibleOnMap !== false ? 'filled' : 'outlined'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Link to={`/category/${id}`}>
                          <Button variant="outlined" size="small">
                            Edit
                          </Button>
                        </Link>
                        <DeleteButton
                          callBack={() => {
                            handleDeleteCategory(id);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </SortableItem>
                ))}
              </SortableList>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Categories;
